/**
 * Zustand Selectors — pre-built hooks for surgical re-renders.
 *
 * Components use these instead of destructuring entire stores.
 * Each selector only triggers a re-render when its specific output changes.
 *
 * IMPORTANT: Selectors must return STABLE references for Zustand's Object.is
 * equality check. Never return a bare `[]` — use module-level constants.
 */

import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { useTaskStore } from "./taskStore";
import { useConnectionStore } from "./connectionStore";
import { taskLiveSignature } from "@/hooks/useTaskFiltering";
import {
  helperReportReason,
  isLiveHelperStatus,
  teammateStatus,
  type TeammateStatus,
} from "@/lib/teammates";
import {
  isLiveWord,
  laneSpan,
  laneTimelineFromThreads,
  laneTitle,
  laneWord,
  type LaneEvent,
  type LaneWord,
} from "@/lib/team-state";
import { familyOf, type Family } from "@/components/task-story/derive";
import { isToolUseBlock } from "@/types";
import type {
  TaskState,
  Message,
  PendingInteraction,
  TaskTodoGroup,
} from "./types";
import type { DisplayStatus } from "@bash/shared";

// Stable empty-array singletons — prevents Object.is from seeing new refs
const EMPTY_MESSAGES: Message[] = [];
const EMPTY_OUTPUTS: import("@bash/shared").TaskOutput[] = [];
// Stable empty per-lane record — prevents a fresh `{}` ref each render.
const EMPTY_LANES: Record<string, PendingInteraction> = {};
const EMPTY_STRINGS: string[] = [];
const EMPTY_TODO_ROWS: TaskTodoGroup["todos"] = [];

// =============================================================================
// By-ID selectors — content-rendering path.
//
// These read `tasks.get(taskId)` directly and never depend on the store's
// `selectedTaskId`. Consumers that live under a URL with a task segment (the
// conversation page) MUST use these, not the `useSelectedTask*` variants —
// otherwise navigating between tasks can flash the previous task's content
// during the frame where the store's selectedTaskId hasn't caught up to the
// new URL. Tracked in: 2026-04-20 "clicking a task shows previous task's
// content" bug, rooted in content-path reads of the lagged store value.
//
// `useSelectedTask*` variants below are for UI chrome (sidebar highlight,
// header breadcrumbs, etc.) where "currently selected" is the right concept.
// =============================================================================

/** Messages for a specific task by id. URL-driven — not store-selected. */
export function useTaskMessages(taskId: string | null | undefined): Message[] {
  return useTaskStore(
    useShallow((s) => {
      if (!taskId) return EMPTY_MESSAGES;
      const task = s.tasks.get(taskId);
      if (!task) return EMPTY_MESSAGES;
      if (!task.streamingMessage) return task.messages || EMPTY_MESSAGES;
      return [...task.messages, task.streamingMessage];
    })
  );
}

/** Whether a specific task is actively processing. */
export function useTaskIsProcessing(
  taskId: string | null | undefined
): boolean {
  return useTaskStore((s) => {
    if (!taskId) return false;
    const task = s.tasks.get(taskId);
    return (
      task?.phase === "processing" ||
      task?.phase === "waiting" ||
      task?.phase === "interrupting" ||
      false
    );
  });
}

/** Pending interaction for a specific task (or null). Main (`''`) lane only —
 *  back-compat: subagent lanes live in `pendingByLane` (see useTaskLaneInteractions). */
export function useTaskPendingInteraction(
  taskId: string | null | undefined
): PendingInteraction | null {
  return useTaskStore((s) => {
    if (!taskId) return null;
    return s.tasks.get(taskId)?.pendingInteraction ?? null;
  });
}

/**
 * T9 — the per-lane SUBAGENT pending map for a task (keyed by `subagentId`).
 * Empty `{}` when no subagent is paused. The DelegationCard subscribes to this
 * (by task id) and matches a lane's `block.toolUseId` against the leaf
 * `subagentId` / `subagentPath` to render an attributed inline pause. The main
 * agent's `''` lane is NOT here — it stays in `pendingInteraction`.
 */
export function useTaskLaneInteractions(
  taskId: string | null | undefined
): Record<string, PendingInteraction> {
  return useTaskStore(
    useShallow((s) => {
      if (!taskId) return EMPTY_LANES;
      return s.tasks.get(taskId)?.pendingByLane ?? EMPTY_LANES;
    })
  );
}

/**
 * T9 — match an Agent/Task tool_use id against the pending subagent lanes.
 *   - depth-1: the leaf `subagentId` (the lane key) IS this tool_use id.
 *   - deeper: a descendant paused and the pause bubbled up; the ancestor's
 *     `agentToolUseId` appears in the attribution path.
 * Pure — shared by the DelegationCard/SubagentBlock renderers and the
 * tool_result pause-sentinel guard (streamingHandlers).
 */
export function findLanePending(
  toolUseId: string,
  lanes: Record<string, PendingInteraction>
): PendingInteraction | null {
  const direct = lanes[toolUseId];
  if (direct) return direct;
  for (const pi of Object.values(lanes)) {
    if (pi.subagentPath?.some((s) => s.agentToolUseId === toolUseId)) return pi;
  }
  return null;
}

// =============================================================================
// Task Selectors
// =============================================================================

/** The currently selected task (or null). Uses shallow comparison to prevent
 *  useSyncExternalStore tearing when the store updates between render and commit. */
export function useSelectedTask(): TaskState | null {
  return useTaskStore(
    useShallow((s) => {
      if (!s.selectedTaskId) return null;
      return s.tasks.get(s.selectedTaskId) ?? null;
    })
  );
}

/**
 * The id of the currently selected task (or null).
 *
 * `selectedTaskId` is a top-level store primitive, so this selector is
 * referentially stable across streaming deltas — the tasks Map churns ~60x/s
 * during a turn, but the selected id only changes on navigation. UI chrome that
 * needs ONLY the id (not the task object) MUST use this instead of
 * `useSelectedTask()`, whose whole-object subscription re-renders every frame
 * while streaming and defeats downstream React.memo.
 */
export function useSelectedTaskId(): string | null {
  return useTaskStore((s) => s.selectedTaskId ?? null);
}

/**
 * Messages for the current task (committed + streaming).
 *
 * Uses useShallow to prevent useSyncExternalStore tearing — the spread creates
 * a new array reference every call, but useShallow caches the result when all
 * elements are referentially equal. This means:
 * - Two getSnapshot() calls with same state → same cached ref → no tearing
 * - State change (delta flush) → new streamingMessage ref → NOT shallow-equal → re-render
 */
export function useCurrentMessages(): Message[] {
  return useTaskStore(
    useShallow((s) => {
      if (!s.selectedTaskId) return EMPTY_MESSAGES;
      const task = s.tasks.get(s.selectedTaskId);
      if (!task) return EMPTY_MESSAGES;
      if (!task.streamingMessage) return task.messages || EMPTY_MESSAGES;
      return [...task.messages, task.streamingMessage];
    })
  );
}

/** Whether the selected task is actively processing. */
export function useIsProcessing(): boolean {
  return useTaskStore((s) => {
    if (!s.selectedTaskId) return false;
    const task = s.tasks.get(s.selectedTaskId);
    return (
      task?.phase === "processing" ||
      task?.phase === "waiting" ||
      task?.phase === "interrupting" ||
      false
    );
  });
}

/** Whether the selected task has extended thinking in progress. */
export function useIsThinking(): boolean {
  return useTaskStore((s) => {
    if (!s.selectedTaskId) return false;
    const task = s.tasks.get(s.selectedTaskId);
    if (!task?.streamingMessage) return false;
    const content = task.streamingMessage.content;
    return (
      content.length > 0 && content[content.length - 1]?.type === "thinking"
    );
  });
}

// =============================================================================
// Field-specific selectors — return primitives to avoid tearing on streaming
// =============================================================================

/** Phase of the selected task (or null). Returns a string — stable across streaming deltas. */
export function useSelectedTaskPhase(): TaskState["phase"] | null {
  return useTaskStore((s) => {
    if (!s.selectedTaskId) return null;
    return s.tasks.get(s.selectedTaskId)?.phase ?? null;
  });
}

/** Pending interaction for the selected task (or null). Stable ref — only changes when a new question/auth event arrives.
 *  Back-compat: returns the MAIN-agent (`''`) lane only, so a subagent pause never flips the global InputStation. */
export function useSelectedTaskPendingInteraction(): PendingInteraction | null {
  return useTaskStore((s) => {
    if (!s.selectedTaskId) return null;
    return s.tasks.get(s.selectedTaskId)?.pendingInteraction ?? null;
  });
}

/**
 * T9 — pending interaction for ONE lane of the selected task.
 *   - `''` / undefined ⇒ the main agent ⇒ `pendingInteraction` (back-compat).
 *   - a `subagentId`   ⇒ that subagent's lane in `pendingByLane`.
 * Returns a stable ref (only changes when that specific lane's pending changes).
 */
export function useLanePendingInteraction(
  subagentId: string | null | undefined
): PendingInteraction | null {
  return useTaskStore((s) => {
    if (!s.selectedTaskId) return null;
    const task = s.tasks.get(s.selectedTaskId);
    if (!task) return null;
    return (
      (subagentId
        ? task.pendingByLane?.[subagentId]
        : task.pendingInteraction) ?? null
    );
  });
}

/** Whether the selected task has an active streaming message. Returns boolean — stable during streaming. */
export function useSelectedTaskHasStreamingMessage(): boolean {
  return useTaskStore((s) => {
    if (!s.selectedTaskId) return false;
    return !!s.tasks.get(s.selectedTaskId)?.streamingMessage;
  });
}

/** Title of the selected task (or null). Returns a string — stable during streaming. */
export function useSelectedTaskTitle(): string | null {
  return useTaskStore((s) => {
    if (!s.selectedTaskId) return null;
    return s.tasks.get(s.selectedTaskId)?.title ?? null;
  });
}

/** Task outputs (files/images) for the selected task. */
export function useSelectedTaskOutputs(): import("@bash/shared").TaskOutput[] {
  return useTaskStore(
    useShallow((s) => {
      if (!s.selectedTaskId) return EMPTY_OUTPUTS;
      return s.tasks.get(s.selectedTaskId)?.outputs ?? EMPTY_OUTPUTS;
    })
  );
}

/** Whether ANY task across the user's session is actively working. */
export function useIsAnyTaskActive(): boolean {
  return useTaskStore((s) => {
    for (const task of s.tasks.values()) {
      if (task.phase === "processing" || task.phase === "waiting") return true;
    }
    return false;
  });
}

/**
 * The live tasks Map for the dashboard — subscribed via a per-task display
 * signature so the home page re-renders ONLY when display-relevant state changes
 * (phase, counts, title, attention, running-tool label, …), never on the ~60fps
 * streaming-token churn that rebuilds the Map on every delta flush.
 *
 * Subscribing to `s.tasks` directly re-rendered the whole dashboard and re-ran
 * useTaskFiltering every frame of every turn (the home-screen analogue of the
 * MessageItem bug). Here we subscribe to a shallow array of `taskLiveSignature`
 * values and read the Map imperatively: between signature changes the Map's
 * display-relevant data is unchanged, so the returned ref is never stale for
 * anything the dashboard actually renders. When a signature does change the
 * subscription re-renders and getState() yields the fresh Map.
 */
export function useDashboardTasks(): Map<string, TaskState> {
  // Reactive subscription — governs when the dashboard re-renders.
  useTaskStore(
    useShallow((s) => {
      const sigs: string[] = [];
      for (const [id, task] of s.tasks) {
        // P5.4 — hidden teammate lanes are excluded from the signature so their
        // streaming churn never re-renders the dashboard.
        if (task.teammateMeta) continue;
        sigs.push(`${id}${taskLiveSignature(task)}`);
      }
      return sigs;
    })
  );
  // Non-reactive read of the actual data to render. Hidden teammate lanes
  // (P5.4) never surface on the dashboard — filter only when one exists so
  // the common case keeps the stable Map ref.
  const tasks = useTaskStore.getState().tasks;
  let hasTeammateLane = false;
  for (const task of tasks.values()) {
    if (task.teammateMeta) {
      hasTeammateLane = true;
      break;
    }
  }
  if (!hasTeammateLane) return tasks;
  const visible = new Map<string, TaskState>();
  for (const [id, task] of tasks) {
    if (!task.teammateMeta) visible.set(id, task);
  }
  return visible;
}

/**
 * P5.4 (Agent Teams) — the LIVE teammates of a task, with per-lane status
 * derived from each teammate's hidden TaskState. Drives the composer's @name
 * mention source. Subscribes via encoded signature strings (stable across
 * unrelated streaming churn); the parsed array is memoized on that signature.
 */
export interface LiveTeammate {
  teammateTaskId: string;
  name: string;
  status: TeammateStatus;
  /**
   * Phase 2 (background helpers) — 'helper' on a background one-shot's lane,
   * absent on a teammate's. Read off the lane's own teammateMeta (the parent's
   * roster entry carries only the handle).
   */
  kind?: "teammate" | "helper";
  /**
   * Phase 3 (spec 2026-09-05 §4.6, D8) — the person can message this helper, so the
   * composer's @ list offers it. Absent on a Phase 2 helper (which stays filtered out)
   * and on a teammate, which the @ list has always listed on `kind` alone.
   */
  addressable?: boolean;
  /**
   * Phase 3 — why an ENDED helper's run finished, read off its report on the lead's
   * thread. Present only while the lane is idle: a live lane reads its `status`
   * instead. The raw wire reason, so the words stay with the one derivation that
   * owns them (`endedWord` in `lib/team-state.ts`) and an unknown future reason
   * still arrives.
   */
  helperStatus?: string;
  /**
   * R37 — has this helper's lane produced anything yet? EVERY lane is minted at
   * `phase: 'idle'` (the live spawn and the reload rebuild alike) and its first turn
   * is enqueued AFTER the spawn event, so idle alone cannot mean "over": a helper
   * spawned a moment ago would read as finished. Its own thread being non-empty is
   * the cheapest proof it ever ran. Helper lanes only.
   */
  hasRun?: boolean;
  /**
   * R37 (the C1 belt, as `helperEndingLine` has it) — the lane's own terminal saying
   * the run was cut short. It covers the ending whose REPORT never reached this
   * client, which would otherwise read as the clean finish it was not. A report that
   * did arrive always wins. Helper lanes only.
   */
  endedInterrupted?: boolean;
}

export function useLiveTeammates(
  taskId: string | null | undefined
): LiveTeammate[] {
  const sigs = useTaskStore(
    useShallow((s) => {
      if (!taskId) return EMPTY_STRINGS;
      const task = s.tasks.get(taskId);
      if (!task?.teammates) return EMPTY_STRINGS;
      const out: string[] = [];
      for (const [teammateTaskId, info] of Object.entries(task.teammates)) {
        const lane = s.tasks.get(teammateTaskId);
        const meta = lane?.teammateMeta;
        const status = teammateStatus(lane);
        // The ending only exists for a lane that is OVER, and reading it costs a scan
        // of the LEAD's thread — so it is asked for exactly where it is shown: an idle
        // helper's row. A working lane's churn never pays for it.
        const ended =
          meta?.kind === "helper" && status === "idle"
            ? (helperReportReason(task.messages, teammateTaskId) ?? "")
            : "";
        // R37 — the two bits that say whether an idle helper is actually OVER, both
        // read straight off the lane this loop already holds (no second scan). Carried
        // for helper lanes only: a teammate is never "over", so its row keeps the exact
        // shape it has always had.
        const isHelper = meta?.kind === "helper";
        const hasRun = isHelper && (lane?.messages?.length ?? 0) > 0;
        const endedInterrupted =
          isHelper && lane?.turnEndedInterrupted === true;
        // `ended` is the only FREE-FORM field here (a reason string off the wire),
        // so it goes LAST: a separator embedded in it can then only truncate itself,
        // never shift every field after it onto the wrong name.
        out.push(
          `${teammateTaskId}\x01${info.name}\x01${status}\x01${
            meta?.kind ?? ""
          }\x01${meta?.addressable ? "1" : ""}\x01${hasRun ? "1" : ""}\x01${
            endedInterrupted ? "1" : ""
          }\x01${ended}`
        );
      }
      return out;
    })
  );
  return useMemo(
    () =>
      sigs.map((sig) => {
        const [
          teammateTaskId,
          name,
          status,
          kind,
          addressable,
          hasRun,
          endedInterrupted,
          helperStatus,
        ] = sig.split("\x01");
        // Conditional spreads, never `addressable: undefined`: a teammate row keeps
        // the exact shape it has always had.
        return {
          teammateTaskId,
          name,
          status: status as TeammateStatus,
          ...(kind ? { kind: kind as LiveTeammate["kind"] } : {}),
          ...(addressable ? { addressable: true } : {}),
          ...(helperStatus ? { helperStatus } : {}),
          ...(hasRun ? { hasRun: true } : {}),
          ...(endedInterrupted ? { endedInterrupted: true } : {}),
        };
      }),
    [sigs]
  );
}

/**
 * Phase 2 (background helpers) — the live HELPER lanes of a task. Helpers ride
 * the same rail as teammates (their own hidden TaskState), so this is that list
 * narrowed to `kind: 'helper'`: what the helpers bar shows and stops.
 */
export function useLiveHelpers(taskId: string | null | undefined) {
  const teammates = useLiveTeammates(taskId);
  return useMemo(
    () => teammates.filter((t) => t.kind === "helper"),
    [teammates]
  );
}

/**
 * Phase 2 (D11, R82) — how many of this task's helpers are still LIVE: working,
 * or waiting on the person. Derived from the live lanes, never from a count the
 * server sent; the story's count line, the composer's Stop and the
 * "Stop them too?" ask all hang off this number. The COPY still says "working"
 * (a paused helper is not working, but it has not finished either).
 */
export function useLiveHelperCount(taskId: string | null | undefined): number {
  const helpers = useLiveHelpers(taskId);
  return helpers.filter((h) => isLiveHelperStatus(h.status)).length;
}

/**
 * R79 — the reason this helper's report carried, for the lane's ending line.
 * The report row lives on the LEAD's thread, so the lane reads across to its
 * parent. Returns a primitive, so a lead's streaming churn re-renders the card
 * only when the reason itself changes.
 */
export function useHelperStopReason(
  parentTaskId: string | null | undefined,
  helperTaskId: string
): string | undefined {
  return useTaskStore((s) =>
    parentTaskId
      ? helperReportReason(s.tasks.get(parentTaskId)?.messages, helperTaskId)
      : undefined
  );
}

/** Count of tasks actively processing (for "N Active" display).
 *  Reads from connectionStore — sandbox is the SOT for its own state.
 *  Server relays activeTaskCount from SANDBOX_STATE_CHANGE → CONNECTION_STATE. */
export function useActiveTaskCount(): number {
  return useConnectionStore((s) => s.activeTaskCount);
}

/** Whether the sandbox is currently upgrading (image or env mismatch detected).
 *  Reads from connectionStore — sandbox is the SOT for its own state. */
export function useIsSandboxUpgrading(): boolean {
  return useConnectionStore((s) => s.connectionState.status === "updating");
}

// =============================================================================
// Agent Display Status
// =============================================================================

/**
 * Sandbox state for UI display.
 *
 * Pure passthrough of the presence-derived status. The server's
 * presence.service.ts is the SINGLE source of truth for what the sandbox
 * is doing — including 'browsing' when a browser session is open. This
 * selector adds no client-side overlay; doing so would create a parallel
 * state machine that drifts from the server's authoritative view.
 *
 * The lifecycle the user sees:
 *
 *   no browser:    standby → starting → idle           (sandbox warming up)
 *   open browser:  standby → starting → browsing       (warmup → live session)
 *   close browser: browsing → idle                     (sandbox stays warm)
 *   agent active:  any → active                        (work always wins)
 *
 * 'browsing' is produced by deriveLegacyStateFromPresence (presenceStore.ts)
 * when phase='ready' AND presence.hasBrowserSession=true. The server populates
 * that field from sandbox-relay's pending+active browser-session sets, kept in
 * sync via recomputeAndBroadcast on every browser-state transition.
 */
export function useAgentDisplayStatus(): DisplayStatus {
  return useConnectionStore((s) => {
    if (s.connectionState.status === "loading")
      return "offline" as DisplayStatus;
    return s.connectionState.status as DisplayStatus;
  });
}

// =============================================================================
// The bench's lanes (spec 2026-09-06 §3.3, D4)
//
// One derivation for every word a team surface says about a lane. The bench,
// the lane's own screen, the return line and the composer's @ list all read a
// LaneState; none of them keeps a word of its own.
// =============================================================================

export interface LaneState {
  teammateTaskId: string;
  /** What a person reads for this lane (§3.4): a helper's brief, a teammate's @name. */
  title: string;
  /** The roster handle, no '@' — what a send to this lane is prefixed with. */
  handle: string;
  /**
   * §3.5 — the lead's delegating block for this lane, which is where its
   * timeline starts. Carried because a reader that only has the roster (the
   * story's older lines, a return line) can then name the block this lane came
   * from without redoing the marker-over-roster fallback below.
   */
  parentToolUseId: string;
  kind: "helper" | "teammate";
  /** Can the person message it? A teammate always; a helper only if its spawn said so. */
  addressable: boolean;
  word: LaneWord;
  /** Working, waiting on the person, or still starting up — anything but over. */
  live: boolean;
  /** The lane's place in the roster, which is its tint (KinFace takes it mod 6). */
  kin: number;
  /** §3.5 — the lead's clock: when it was sent off, when it last reported back. */
  spawnedAt: number | null;
  endedAt: number | null;
  /**
   * §3.5, R17 — when its LAST run opened (the spawn, or the resume that sent it
   * out again). Every clock a person reads for this lane measures from here:
   * the lifetime is not the run, and a helper picked back up hours later read
   * "done · 361m" for a run that took seconds.
   */
  runStartedAt: number | null;
  /**
   * §3.5, R25 — the lane's whole life, in order: sent off, back, out again,
   * back. Built HERE and carried, because this is the one reader holding both
   * threads the events live on (the lead's for the sending and the coming back,
   * the lane's own for the sending out again). The three fields above are its
   * summary; a story line that has to ask about a MOMENT (who was out when this
   * turn ended, which run this report closed) reads the events themselves.
   */
  timeline: LaneEvent[];
}

const EMPTY_LANE_STATES: LaneState[] = [];

/**
 * The lane's newest live step, as the story's own families name it: the last
 * assistant message's last tool block that is still RUNNING. Null between
 * steps, which `laneWord` reads as the generic "working".
 *
 * R12 — the marker is `status`, the store's own (`derive.ts`'s `lastRunning`
 * asks the same question the same way), never a missing `result`: the settle
 * writes `JSON.stringify(data.content)` (`streamingToolHandlers.ts`), and
 * `JSON.stringify(undefined)` is `undefined`, so a tool that completes with no
 * content leaves a finished block looking live.
 */
function liveFamilyOf(lane: TaskState | undefined): Family | null {
  const messages = lane?.messages;
  if (!messages?.length) return null;
  let last: Message | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant") {
      last = messages[i];
      break;
    }
  }
  if (!last) return null;
  for (let i = last.content.length - 1; i >= 0; i--) {
    const block = last.content[i];
    if (isToolUseBlock(block) && block.status === "running")
      return familyOf(
        block.toolName,
        block.display,
        (block.toolInput ?? null) as Record<string, unknown> | null
      );
  }
  return null;
}

/**
 * The signature separator, and its one rule: the FREE-FORM field goes LAST. A
 * lane's title is a description off the wire, so it is the only field that can
 * carry a separator of its own; last, it can only run into itself rather than
 * shifting a later field onto the wrong value. Same rule, same reason, as
 * `useLiveTeammates` above.
 */
const SEP = "\x01";

/**
 * Everything a reader can SEE about a lane, in one string.
 *
 * The clock is deliberately not in it: the bench ticks that itself
 * (`useNow1s`), and a second in here would re-render every reader once a second
 * for as long as a lane is out. The timeline rides as a digest — its length and
 * its newest beat — because a new event always lengthens it, and holding the
 * whole history in a comparison string is a cost paid on every store update.
 */
function laneSignature(l: LaneState): string {
  const last = l.timeline[l.timeline.length - 1];
  return [
    l.teammateTaskId,
    l.kind,
    l.addressable ? "1" : "",
    l.word.kind,
    l.word.word,
    l.spawnedAt ?? "",
    l.endedAt ?? "",
    l.runStartedAt ?? "",
    l.handle,
    l.parentToolUseId,
    l.timeline.length,
    last ? `${last.at}:${last.kind}` : "",
    l.title,
  ].join(SEP);
}

/** §3.3 — the walk itself: every lane of a task, in roster order. */
function walkLanes(
  tasks: ReadonlyMap<string, TaskState>,
  taskId: string
): LaneState[] {
  const task = tasks.get(taskId);
  if (!task?.teammates) return EMPTY_LANE_STATES;
  const out: LaneState[] = [];
  for (const [teammateTaskId, info] of Object.entries(task.teammates)) {
    const lane = tasks.get(teammateTaskId);
    const meta = lane?.teammateMeta;
    const kind = meta?.kind === "helper" ? "helper" : "teammate";
    // The lane's own marker wins; the roster's block id is what a lane minted
    // from a message that RACED the spawn has instead (TeammateMeta documents
    // the empty string it carries then), so the fallback is not decoration —
    // without it such a lane has no clock at all.
    const parentToolUseId = meta?.parentToolUseId || info.toolUseId;
    // R25 — BOTH threads. The lead's records the sending and the coming back;
    // the sending out AGAIN only ever lands on the lane's own thread, so a
    // timeline built from the lead alone never saw a resume and every clock
    // stayed the lifetime.
    const timeline = laneTimelineFromThreads({
      leadMessages: task.messages,
      laneMessages: lane?.messages,
      laneId: teammateTaskId,
      parentToolUseId,
    });
    const { spawnedAt, endedAt, runStartedAt } = laneSpan(timeline);
    const stopReason = helperReportReason(task.messages, teammateTaskId);
    // A COLD lane: on a reload `rebuildTeammateLanes` mints every lane
    // `phase: 'idle'` with no messages and asks for its thread over the
    // socket, so for that window the lane's own thread cannot say whether it
    // ever ran. `laneWord`'s empty-thread branch reads that silence as
    // `starting`, which counts LIVE — and a team that finished days ago
    // would paint as starting and grow a "Stop all" whose fan-out reaches
    // lanes that already reported.
    //
    // The LEAD's thread is the older, colder record and it already knows: a
    // RETURN is proof the lane ran, so the thread counts as run and the word
    // is the ending it earned. A lane with none is genuinely starting.
    //
    // The return, and not the report's stop reason: a lane sent out again is
    // out once more, and `laneSpan` says so by nulling `endedAt` at the resume
    // — while the old reason sits on the lead's thread forever. Reading that
    // reason painted a running helper "done" and left it out of Stop all.
    const ran = endedAt !== null;
    const threadLength = lane?.messages?.length ?? 0;
    const word = laneWord({
      phase: lane?.phase,
      pendingInteraction: lane?.pendingInteraction,
      threadLength: ran ? Math.max(threadLength, 1) : threadLength,
      liveFamily: liveFamilyOf(lane),
      stopReason,
      endedInterrupted: lane?.turnEndedInterrupted === true,
    });
    out.push({
      teammateTaskId,
      title: laneTitle({
        kind,
        description: meta?.description,
        name: info.name,
      }),
      handle: info.name,
      parentToolUseId,
      kind,
      addressable: meta?.addressable === true || kind === "teammate",
      word,
      live: isLiveWord(word),
      // The lane's place in the roster, which is its tint.
      kin: out.length,
      spawnedAt,
      endedAt,
      runStartedAt,
      timeline,
    });
  }
  return out;
}

/**
 * I1 — ONE walk per store update, whoever asks.
 *
 * `useLaneStates` lives inside `useDeskState`, which EVERY `StreamDesk` calls —
 * one per turn in the column. A selector runs per subscriber per store change,
 * so a thirty-turn story ran the walk thirty times for the same answer on every
 * streaming delta, before a single line rendered.
 *
 * The key is the `tasks` Map itself: immer finalizes a new one on every write
 * through `_mutateTasks`, and hands the same one back when a `set` touched
 * something else. So a hit means nothing this reading depends on has changed —
 * the strongest cache key there is, and it cannot go stale.
 */
const LANES_BY_TASKS = new WeakMap<
  ReadonlyMap<string, TaskState>,
  Map<string, LaneState[]>
>();

/**
 * The previous answer per lead task, so an unchanged lane keeps its OBJECT.
 *
 * The WeakMap above makes the walk cheap; this makes its RESULT stable. Every
 * store write mints a new `tasks` Map, so without this each walk would hand
 * back fresh objects and re-render every reader of the bench on every delta —
 * which is what the signature string existed to prevent. Signatures still
 * decide, they just travel beside the lane now instead of standing in for it.
 */
const LAST_LANES = new Map<string, { sigs: string[]; lanes: LaneState[] }>();

export function laneStatesOf(
  tasks: ReadonlyMap<string, TaskState>,
  taskId: string | null | undefined
): LaneState[] {
  if (!taskId) return EMPTY_LANE_STATES;
  let byTask = LANES_BY_TASKS.get(tasks);
  if (!byTask) LANES_BY_TASKS.set(tasks, (byTask = new Map()));
  const hit = byTask.get(taskId);
  if (hit) return hit;

  const walked = walkLanes(tasks, taskId);
  const sigs = walked.map(laneSignature);
  const prev = LAST_LANES.get(taskId);
  let lanes = walked;
  if (prev && prev.sigs.length === sigs.length) {
    let moved = false;
    const merged = walked.map((l, i) => {
      if (prev.sigs[i] === sigs[i]) return prev.lanes[i];
      moved = true;
      return l;
    });
    lanes = moved ? merged : prev.lanes;
  }
  // The store itself keeps at most twenty tasks (`_mutateTasks`'s LRU), so a
  // map this size is already past every task that can still be read; dropping
  // it whole costs one render of fresh identities and nothing else.
  if (LAST_LANES.size > 24) LAST_LANES.clear();
  LAST_LANES.set(taskId, { sigs, lanes });
  byTask.set(taskId, lanes);
  return lanes;
}

/**
 * §3.3 — every lane of a task, in roster order, each under its own state word.
 *
 * The array's identity survives a store update that changed nothing about the
 * lanes (`laneStatesOf`), so a lead's streaming churn re-renders the bench only
 * when a word, a title, a clock's anchor or the roster moves.
 */
export function useLaneStates(taskId: string | null | undefined): LaneState[] {
  return useTaskStore((s) => laneStatesOf(s.tasks, taskId));
}

/** One lane by id — the lane's screen and the composer each read the one in view. */
export function useLaneState(
  taskId: string | null | undefined,
  laneId: string | null
): LaneState | null {
  const lanes = useLaneStates(taskId);
  return useMemo(
    () =>
      laneId ? (lanes.find((l) => l.teammateTaskId === laneId) ?? null) : null,
    [lanes, laneId]
  );
}

// =============================================================================
// The freshest plan group (spec 2026-09-06 §3.11, D10)
// =============================================================================

/**
 * The plan a person is looking at: the group holding the HIGHEST turn.
 *
 * Not `todos[0]`. The array is newest-first only while the tab stays open —
 * the live handler prepends. The sync hydrates it in the server's order,
 * oldest first, so after a reload `[0]` is turn ZERO and the plan in hand hid
 * behind a finished one until the next turn wrote a group of its own. Reading
 * the max makes both orders answer the same.
 *
 * A tie (two groups stamped with one turn) goes to the later `createdAt`, so a
 * write that arrived out of order still loses to the newer one. An unparseable
 * stamp counts as the beginning of time rather than throwing the comparison.
 */
export function latestTodoGroup(
  task: Pick<TaskState, "todos"> | null | undefined
): TaskTodoGroup | null {
  const groups = task?.todos;
  if (!groups?.length) return null;
  const at = (g: TaskTodoGroup) => Date.parse(g.createdAt) || 0;
  let best = groups[0];
  for (const g of groups) {
    if (g.turnIndex > best.turnIndex) best = g;
    else if (g.turnIndex === best.turnIndex && at(g) > at(best)) best = g;
  }
  return best;
}

/**
 * The freshest group's rows — what the rail and the composer's peek draw.
 *
 * The group's own array comes straight back, so an update that left the plan
 * alone hands the same reference over and re-renders neither surface.
 */
export function useLatestTodos(
  taskId: string | null | undefined
): TaskTodoGroup["todos"] {
  return useTaskStore((s) =>
    taskId
      ? (latestTodoGroup(s.tasks.get(taskId))?.todos ?? EMPTY_TODO_ROWS)
      : EMPTY_TODO_ROWS
  );
}
